import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { getTrackingData } from "../../utils/trackingConfig";
import { subscriptionData } from "../../redux/slices/subscriptionSlice";
import { subscriptionPayment } from "../../redux/slices/subscriptionPaymentSlice";
import "./ComparisonModal.scss";

const SWIPE_CLOSE_THRESHOLD = 110; // px the sheet must travel down to dismiss
const SWIPE_MOBILE_BREAKPOINT = 576; // px — match the scss @media bp

/* Icons — match the strokes/proportions of the standalone PWIcon set. */
const StethoscopeIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
    <path d="M8 15v1a6 6 0 0 0 6 6v0a5 5 0 0 0 5-5" />
    <circle cx="20" cy="10" r="2" />
  </svg>
);

const SparkIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2 13.6 8.4 20 10l-6.4 1.6L12 18l-1.6-6.4L4 10l6.4-1.6z" />
  </svg>
);

/* Plan-specific copy matching the standalone "lockedRole" trigger. */
const PLAN_COPY = {
  Pro: {
    infoText:
      "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
    trialPrice: "$2.99/month",
    yearlyPrice: "$29.99/year",
    newsletterPrice: "$4.99/month billed annually at $49.99/year",
    newsletterLabel: "Pro + Newsletter",
  },
  Plus: {
    infoText:
      "Pro users see all 9 nursing roles, including NP, CRNA, and Midwife",
    trialPrice: "$2.99/month",
    yearlyPrice: "$29.99/year",
    newsletterPrice: "$4.99/month billed annually at $49.99/year",
    newsletterLabel: "Pro + Newsletter",
  },
};

const ComparisonModal = ({
  // legacy props (kept for backwards compatibility with all call sites)
  title,
  description,
  isVisible,
  massage1,
  pluspricem,
  pluspricey,
  prodesc,
  promassage,
  propricem,
  propricey,
  eventSource,
  onClose,
  onPressYes,
  yesText,
  noText,
  extraData,
  extraMetaInfo,
  // new optional props for the redesigned card
  lockedItemName,
  plan,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const profileStore = useSelector((state) => state.profile);
  const subscriptionState = useSelector((state) => state.subscriptionData);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const hasUsedProTrial =
    user?.has_used_pro_trial ??
    profileStore?.profileItem?.data?.has_used_pro_trial ??
    false;

  const resolvedPlan =
    plan ||
    (propricem || propricey || promassage
      ? "Pro"
      : pluspricem || pluspricey
      ? "Plus"
      : "Pro");

  const copy = PLAN_COPY[resolvedPlan] || PLAN_COPY.Pro;
  const yearly =
    resolvedPlan === "Pro"
      ? propricey || copy.yearlyPrice
      : pluspricey || copy.yearlyPrice;
  const trial =
    resolvedPlan === "Pro"
      ? propricem || copy.trialPrice
      : pluspricem || copy.trialPrice;
  const infoText = promassage || massage1 || copy.infoText;
  /* Strip any trailing "(Plus)" / "(Pro)" the caller may have passed
     in (some pages append it to the dropdown label). This way every
     call site can just forward its raw label and the modal title
     stays clean — "Nurse Midwives is a Plus role" instead of
     "Nurse Midwives (Plus) is a Plus role". */
  const cleanLockedItemName = lockedItemName
    ? String(lockedItemName).replace(/\s*\((?:Plus|Pro)\)\s*$/i, "")
    : lockedItemName;

  const heading = title || (cleanLockedItemName
    ? `${cleanLockedItemName} is a ${resolvedPlan} role`
    : `Upgrade to ${resolvedPlan}`);

  /* Swipe-down-to-close gesture (mobile only). The dialog element gets
     a translateY that follows the finger; if released past the
     threshold we trigger onClose, otherwise it snaps back. */
  const dialogRef = useRef(null);
  const startYRef = useRef(null);
  const draggingRef = useRef(false);
  const [dragY, setDragY] = useState(0);

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.innerWidth <= SWIPE_MOBILE_BREAKPOINT;

  const onTouchStart = (e) => {
    if (!isMobile()) return;
    startYRef.current = e.touches[0].clientY;
    draggingRef.current = true;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current || startYRef.current == null) return;
    const dy = e.touches[0].clientY - startYRef.current;
    if (dy > 0) {
      setDragY(dy);
    } else {
      setDragY(0);
    }
  };
  const onTouchEnd = () => {
    if (!draggingRef.current) return;
    const finalDy = dragY;
    draggingRef.current = false;
    startYRef.current = null;
    if (finalDy > SWIPE_CLOSE_THRESHOLD) {
      setDragY(0);
      onClose && onClose();
    } else {
      setDragY(0);
    }
  };

  // Reset drag offset whenever the modal is closed externally so the
  // sheet doesn't reopen halfway down.
  useEffect(() => {
    if (!isVisible) setDragY(0);
  }, [isVisible]);

  // Apply the drag transform to the visible card (.modal-content) so
  // the whole sheet follows the finger, not just its inner padding.
  useEffect(() => {
    if (!isVisible) return;
    const content = document.querySelector(".upgrade-pro-modal .modal-content");
    if (!content) return;
    if (dragY > 0) {
      content.style.transform = `translateY(${dragY}px)`;
      content.style.transition = "none";
    } else {
      content.style.transform = "";
      content.style.transition = "";
    }
  }, [dragY, isVisible]);

  const handleUpgrade = () => {
    onClose && onClose();
    const { event } = getTrackingData(eventSource);
    navigate("/profile/subscription", { state: { source: eventSource } });
  };

  const handleSkipTrial = async () => {
    let plans = subscriptionState?.subscriptionDataItems || [];
    if (!plans.length) {
      try {
        const result = await dispatch(subscriptionData()).unwrap();
        plans = result?.subscriptionData || [];
      } catch (e) {
        handleUpgrade();
        return;
      }
    }
    const proMonthlyPlan = plans.find(
      (p) =>
        p.name === "Pro" &&
        p.interval === "month" &&
        p.slug !== "pro-newsletter",
    );
    if (!proMonthlyPlan?.price_id) {
      handleUpgrade();
      return;
    }
    const { event } = getTrackingData(eventSource);
    const payload = {
      priceId: proMonthlyPlan.price_id,
      customerEmail: user?.email,
      metadata: {
        userId: user?.id,
        plan_id: proMonthlyPlan.id,
        billingCycle: "month",
        trigger_feature: event,
      },
    };
    setIsCheckingOut(true);
    try {
      const result = await dispatch(subscriptionPayment(payload)).unwrap();
      const checkoutUrl =
        result?.subscriptionPaymentItems?.url ||
        result?.url ||
        result?.data?.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        handleUpgrade();
      }
    } catch (error) {
      console.error("Skip trial checkout failed:", error);
      handleUpgrade();
    } finally {
      setIsCheckingOut(false);
    }
  };

  const handleTrial = () => {
    onClose && onClose();
    navigate("/profile/subscription", {
      state: { source: eventSource, trial: true },
    });
  };

  const handleNewsletter = () => {
    onClose && onClose();
    navigate("/profile/subscription", {
      state: { source: eventSource, newsletter: true },
    });
  };

  return (
    <Modal
      show={isVisible}
      onHide={onClose}
      centered
      backdrop={true}
      dialogClassName="upgrade-pro-modal"
      backdropClassName="upgrade-pro-modal-backdrop"
    >
      <Modal.Body
        ref={dialogRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <div
          className="upm-handle"
          aria-hidden="true"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        />

        <button
          type="button"
          className="upm-close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX size={20} />
        </button>

        <div className="upm-icon-row">
          <div className="upm-icon">
            <StethoscopeIcon className="upm-icon-svg" />
          </div>
        </div>

        <h2 className="upm-title">{heading}</h2>
        <p className="upm-sub">{description || "Upgrade to keep going."}</p>

        <div className="upm-info">
          <SparkIcon className="upm-info-spark" />
          <p className="upm-info-text">{infoText}</p>
        </div>

        <div className="upm-ctas">
          {!hasUsedProTrial ? (
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleTrial}
          >
            <span className="upm-btn-primary-title">Start 3-day free trial</span>
            <span className="upm-btn-primary-sub">
              No charge today. {yearly} after your 3-day trial.
            </span>
          </button>
          ) : (
          <button
            type="button"
            className="upm-btn-primary"
            onClick={handleTrial}
          >
            <span className="upm-btn-primary-title">Get Pro</span>
            <span className="upm-btn-primary-sub">
              {trial}, billed annually at {yearly}. Cancel anytime.
            </span>
          </button>
          )}

          <button
            type="button"
            className="upm-btn-secondary"
            onClick={handleSkipTrial}
            disabled={isCheckingOut}
          >
            {isCheckingOut
              ? "Opening Secure Payment..."
              : hasUsedProTrial
              ? `Buy Pro Plan · ${trial}`
              : `Buy Pro Plan · ${trial}`}
          </button>

          <button
            type="button"
            className="upm-newsletter"
            onClick={handleNewsletter}
          >
            <span className="upm-newsletter-q">
              Want jobs in your inbox too?
            </span>
            <span className="upm-newsletter-cta">
              Try {copy.newsletterLabel} at {copy.newsletterPrice}
            </span>
          </button>
        </div>

        <button type="button" className="upm-later" onClick={onClose}>
          Maybe later
        </button>
      </Modal.Body>
    </Modal>
  );
};

export default ComparisonModal;
